import { cn } from '../../utils';

/** Default display width per artwork (Tailwind); omitted when `width` prop is set. */
const DEFAULT_WIDTH_CLASS: Record<'plain' | 'gradient' | 'compact', string> = {
    plain: 'w-40',
    gradient: 'w-52',
    compact: 'w-12',
};

type ReneoLogoPlainOrGradientProps = {
    className?: string;
    type?: 'plain' | 'gradient';
    /** CSS width (e.g. `160`, `'10rem'`, `'100%'`). Overrides default Tailwind width for this artwork. */
    width?: number | string;
};

type ReneoLogoCompactProps = {
    className?: string;
    type: 'compact';
    /** Light / dark artwork; only applies when `type` is `compact`. */
    mode: 'light' | 'dark';
    /** CSS width (e.g. `48`, `'3rem'`, `'100%'`). Overrides default Tailwind width for compact artwork. */
    width?: number | string;
};

type ReneoLogoProps = ReneoLogoPlainOrGradientProps | ReneoLogoCompactProps;

function logoKind(props: ReneoLogoProps): 'plain' | 'gradient' | 'compact' {
    if (props.type === 'compact') {
        return 'compact';
    }
    const t = props.type ?? 'plain';
    return t === 'gradient' ? 'gradient' : 'plain';
}

function logoSrc(props: ReneoLogoProps): string {
    if (props.type === 'compact') {
        return props.mode === 'dark' ? '/assets/reneo-logo-compact-dark.svg' : '/assets/reneo-logo-compact-light.svg';
    }
    const kind = props.type ?? 'plain';
    if (kind === 'gradient') {
        return '/assets/reneo-logo-gradient.svg';
    }
    return '/assets/reneo-logo-plain.svg';
}

export function ReneoLogo(props: ReneoLogoProps) {
    const kind = logoKind(props);
    const hasWidth = props.width !== undefined;
    const style = hasWidth
        ? {
              width: typeof props.width === 'number' ? `${props.width}px` : props.width,
              height: 'auto' as const,
          }
        : undefined;

    const className = cn('h-auto max-w-full', !hasWidth ? DEFAULT_WIDTH_CLASS[kind] : undefined, props.className);

    return <img src={logoSrc(props)} alt="Reneo" className={className} style={style} />;
}
